import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import type { CatalogIndex, ProductCard } from "@itaas/pep-contracts";
import { getIndustries, getProducts } from "../api/pep-api";
import { IndustryProductCard } from "../components/catalog/IndustryProductCard";
import { PepApiErrorBanner } from "../components/feedback/PepApiErrorBanner";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";
import { SITE_URL } from "../config";
import { DomainIcon, type DomainIconName } from "../home/HomeIcons";
import { usePepFetch } from "../hooks/usePepFetch";
import { usePepI18n } from "../i18n/usePepI18n";
import {
  SECTORS_APPROACH_STEPS,
  SECTORS_HERO_STRIPS,
  SECTORS_PRIMARY,
  SECTORS_PUBLISHABLE_PRODUCT_SLUGS,
  SECTORS_TRANSVERSAL,
  publicLimitedLabel,
  sectorsCopy,
  type PrimarySectorEditorial,
  type SectorProductRef,
} from "../sectors/sectors-content";
import {
  filterTrialEligibleProducts,
  needsBiometricHardware,
} from "../utils/biometric-trial";

type Industry = CatalogIndex["industries"][number];

const TONE_ICON: Record<PrimarySectorEditorial["tone"], DomainIconName> = {
  education: "education",
  mining: "mines",
  health: "health",
  commerce: "ops",
  security: "security",
};

function PillarIcon({ id }: { id: string }) {
  const common = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    "aria-hidden": true as const,
  };
  if (id === "adapted") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }
  if (id === "secure") {
    return (
      <svg {...common}>
        <path d="M12 3 19 6.5v5.2c0 4.2-2.8 7.6-7 8.8-4.2-1.2-7-4.6-7-8.8V6.5L12 3Z" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M4 19V5" />
      <path d="M4 19h16" />
      <path d="M8 16v-5" />
      <path d="M12 16V8" />
      <path d="M16 16v-3" />
    </svg>
  );
}

function TrustIcon({ id }: { id: string }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    "aria-hidden": true as const,
  };
  if (id === "support") {
    return (
      <svg {...common}>
        <path d="M12 3a7 7 0 0 0-7 7v2.2a2.8 2.8 0 0 0 2.8 2.8h.4V12H7.5V10a4.5 4.5 0 0 1 9 0v2h-1.7v3h.4A2.8 2.8 0 0 0 19 12.2V10a7 7 0 0 0-7-7Z" />
      </svg>
    );
  }
  if (id === "secure") {
    return (
      <svg {...common}>
        <path d="M12 3 19 6.5v5.2c0 4.2-2.8 7.6-7 8.8-4.2-1.2-7-4.6-7-8.8V6.5L12 3Z" />
        <path d="M9.5 12.2 11.2 14l3.5-4" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  );
}

function SolutionChips({
  solutions,
  locale,
}: {
  solutions: readonly SectorProductRef[];
  locale: "fr" | "en";
}) {
  const en = locale === "en";
  const limitedLabel = publicLimitedLabel(locale);
  return (
    <ul className="pep-sec-chips">
      {solutions.map((s) => (
        <li key={`${s.slug}-${s.labelFr}`}>
          <Link to={s.href} className="pep-sec-chip">
            {en ? s.labelEn : s.labelFr}
            {s.badge === "limited" ? (
              <span className="pep-sec-chip__badge pep-sec-chip__badge--limited">{limitedLabel}</span>
            ) : null}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function PrimarySectorCard({
  sector,
  locale,
  copy,
}: {
  sector: PrimarySectorEditorial;
  locale: "fr" | "en";
  copy: ReturnType<typeof sectorsCopy>;
}) {
  const en = locale === "en";
  return (
    <article
      className={`pep-sec-card pep-sec-card--${sector.tone}`}
      data-sector={sector.id}
      data-maturity={sector.maturity}
    >
      <div className="pep-sec-card__media">
        <img
          src={sector.imageSrc}
          alt={en ? sector.imageAltEn : sector.imageAltFr}
          width={640}
          height={360}
          loading="lazy"
          decoding="async"
          style={{
            objectPosition: sector.objectPositionDesktop,
          }}
          data-object-desktop={sector.objectPositionDesktop}
          data-object-tablet={sector.objectPositionTablet}
          data-object-mobile={sector.objectPositionMobile}
        />
        <span className="pep-sec-card__icon" aria-hidden="true">
          <DomainIcon name={TONE_ICON[sector.tone]} />
        </span>
      </div>
      <div className="pep-sec-card__body">
        <div className="pep-sec-card__head">
          <h3 className="pep-sec-card__title">{en ? sector.titleEn : sector.titleFr}</h3>
          {sector.showPublicLimitedBadge ? (
            <span className="pep-sec-badge pep-sec-badge--limited">{copy.limitedAvailability}</span>
          ) : null}
        </div>
        {sector.offerBadges ? (
          <div className="pep-sec-offer-badges" aria-label={en ? "Offers" : "Offres"}>
            {sector.offerBadges.map((b) => (
              <Link
                key={b.slug}
                to={b.href}
                className="pep-sec-offer-badge pep-sec-offer-badge--limited"
              >
                {en ? b.labelEn : b.labelFr}
                {b.badge === "limited" ? <span>{copy.limitedAvailability}</span> : null}
              </Link>
            ))}
          </div>
        ) : null}
        <p className="pep-sec-card__summary">{en ? sector.summaryEn : sector.summaryFr}</p>
        <ul className="pep-sec-axes">
          {sector.axes.map((axis) => (
            <li key={axis.titleFr}>
              <strong>{en ? axis.titleEn : axis.titleFr}</strong>
              <span>{en ? axis.bodyEn : axis.bodyFr}</span>
            </li>
          ))}
        </ul>
        <p className="pep-sec-card__sol-label">{copy.keySolutions}</p>
        <SolutionChips solutions={sector.solutions} locale={locale} />
        <Link className="pep-sec-card__cta" to={`/industries/${sector.industrySlug}`}>
          {copy.explore} →
        </Link>
      </div>
    </article>
  );
}

function sectorsJsonLd(locale: "fr" | "en", siteOrigin: string) {
  const en = locale === "en";
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: en ? "ITaaS Suite — Sectors" : "ITaaS Suite — Secteurs",
    description: sectorsCopy(locale).seoDescription,
    url: `${siteOrigin}/industries`,
    inLanguage: en ? "en" : "fr",
    isPartOf: {
      "@type": "WebSite",
      name: "ITaaS Suite",
      url: siteOrigin,
    },
    hasPart: SECTORS_PRIMARY.map((s) => ({
      "@type": "WebPage",
      name: en ? s.titleEn : s.titleFr,
      url: `${siteOrigin}/industries/${s.industrySlug}`,
      description: en ? s.summaryEn : s.summaryFr,
    })),
  };
}

/**
 * Public Sectors list — route remains /industries (canonical).
 * Composition: S-HERO → S-PRIMARY → S-TRANSVERSAL → S-APPROACH → S-CONVERSION
 */
export function IndustriesPage() {
  const { locale } = usePepI18n();
  const copy = sectorsCopy(locale);
  const en = locale === "en";
  const siteOrigin = SITE_URL;

  return (
    <SiteLayout>
      <SeoHead
        title={copy.seoTitle}
        description={copy.seoDescription}
        keywords={[...copy.seoKeywords]}
        canonicalPath="/industries"
        locale={locale}
        jsonLd={sectorsJsonLd(locale, siteOrigin)}
      />

      <section className="pep-sec-hero" data-section="S-HERO">
        <div className="container pep-sec-hero__grid">
          <div className="pep-sec-hero__copy">
            <h1 className="pep-sec-hero__title">
              {copy.heroTitleBefore}
              <span className="pep-sec-hero__emphasis">{copy.heroTitleEmphasis}</span>
              {copy.heroTitleAfter}
            </h1>
            <p className="pep-sec-hero__lead">{copy.heroLead}</p>
            <ul className="pep-sec-pillars">
              {copy.pillars.map((p) => (
                <li key={p.id}>
                  <span className="pep-sec-pillars__icon">
                    <PillarIcon id={p.id} />
                  </span>
                  <span>
                    <strong>{p.title}</strong>
                    <span>{p.body}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="pep-sec-hero__mosaic" aria-hidden="false">
            {SECTORS_HERO_STRIPS.map((strip) => (
              <figure
                key={strip.id}
                className={`pep-sec-hero__strip pep-sec-hero__strip--${strip.id}`}
              >
                <img
                  src={strip.imageSrc}
                  alt={en ? strip.altEn : strip.altFr}
                  width={280}
                  height={420}
                  loading="eager"
                  decoding="async"
                  style={{ objectPosition: strip.objectPositionDesktop }}
                  data-object-desktop={strip.objectPositionDesktop}
                  data-object-tablet={strip.objectPositionTablet}
                  data-object-mobile={strip.objectPositionMobile}
                />
                <figcaption>
                  <strong>{en ? strip.labelEn : strip.labelFr}</strong>
                  <span>{en ? strip.captionEn : strip.captionFr}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="pep-sec-primary" data-section="S-PRIMARY-SECTORS">
        <div className="container">
          <div className="pep-sec-section-head">
            <p className="pep-sec-eyebrow">{copy.primaryEyebrow}</p>
            <h2 className="pep-sec-section-title">
              {copy.primaryTitleBefore}
              <span className="pep-sec-hero__emphasis">{copy.primaryTitleEmphasis}</span>
              {copy.primaryTitleAfter}
            </h2>
            <p className="pep-sec-section-lead">{copy.primaryLead}</p>
          </div>
          <div className="pep-sec-primary__layout">
            <p className="pep-sec-rail" aria-hidden="true">
              {copy.primaryEyebrow}
            </p>
            <div className="pep-sec-primary__grid">
              {SECTORS_PRIMARY.map((sector) => (
                <PrimarySectorCard key={sector.id} sector={sector} locale={locale} copy={copy} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pep-sec-transversal" data-section="S-TRANSVERSAL-SECTORS">
        <div className="container">
          <div className="pep-sec-section-head">
            <p className="pep-sec-eyebrow">{copy.transversalEyebrow}</p>
            <h2 className="pep-sec-section-title">{copy.transversalTitle}</h2>
            <p className="pep-sec-section-lead">{copy.transversalLead}</p>
          </div>
          <div className="pep-sec-transversal__layout pep-sec-transversal__layout--no-legend">
            <p className="pep-sec-rail" aria-hidden="true">
              {copy.transversalEyebrow}
            </p>
            <div className="pep-sec-transversal__grid">
              {SECTORS_TRANSVERSAL.map((t) => (
                <article
                  key={t.id}
                  className="pep-sec-xcard"
                  data-sector={t.id}
                  data-maturity={t.maturity}
                >
                  <div className="pep-sec-xcard__head">
                    <h3 className="pep-sec-xcard__title">{en ? t.titleEn : t.titleFr}</h3>
                    {t.showPublicLimitedBadge ? (
                      <span className="pep-sec-badge pep-sec-badge--limited">
                        {copy.limitedAvailability}
                      </span>
                    ) : null}
                  </div>
                  <p className="pep-sec-xcard__summary">{en ? t.summaryEn : t.summaryFr}</p>
                  <SolutionChips solutions={t.solutions} locale={locale} />
                  {t.industrySlug ? (
                    <Link className="pep-sec-xcard__cta" to={`/industries/${t.industrySlug}`}>
                      {copy.explore} →
                    </Link>
                  ) : (
                    <Link className="pep-sec-xcard__cta" to="/products/rh">
                      {copy.explore} →
                    </Link>
                  )}
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pep-sec-approach" data-section="S-APPROACH">
        <div className="container pep-sec-approach__grid">
          <div>
            <h2 className="pep-sec-section-title">{copy.approachTitle}</h2>
            <p className="pep-sec-section-lead">{copy.approachLead}</p>
            <ul className="pep-sec-benefits">
              {copy.approachBenefits.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>
          <ol className="pep-sec-steps">
            {SECTORS_APPROACH_STEPS.map((step, i) => (
              <li key={step.id}>
                <span className="pep-sec-steps__num">{i + 1}</span>
                <div>
                  <strong>{en ? step.titleEn : step.titleFr}</strong>
                  <p>{en ? step.bodyEn : step.bodyFr}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="pep-sec-conversion" data-section="S-CONVERSION">
        <div className="container pep-sec-conversion__box">
          <div>
            <h2 className="pep-sec-conversion__title">{copy.conversionTitle}</h2>
            <p className="pep-sec-conversion__lead">{copy.conversionLead}</p>
            <div className="pep-sec-conversion__actions">
              <Link className="btn pep-sec-btn-primary" to={copy.contactHref}>
                {copy.ctaExpert} →
              </Link>
              <Link className="btn pep-sec-btn-secondary" to={copy.solutionsHref}>
                {copy.ctaSolutions}
              </Link>
            </div>
            <ul className="pep-sec-trust">
              {copy.trust.map((t) => (
                <li key={t.id}>
                  <TrustIcon id={t.id} />
                  <span>{t.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function publishableRelated(industry: Industry, products: ProductCard[]): ProductCard[] {
  return products.filter(
    (p) =>
      industry.products.includes(p.slug) &&
      SECTORS_PUBLISHABLE_PRODUCT_SLUGS.has(p.slug) &&
      p.publicCatalog !== false,
  );
}

function editorialForIndustry(slug: string) {
  return SECTORS_PRIMARY.find((s) => s.industrySlug === slug);
}

export function IndustryDetailPage() {
  const { slug = "" } = useParams();
  const { locale } = usePepI18n();
  const copy = sectorsCopy(locale);
  const en = locale === "en";
  const { data: industries, loading, error, reload } = usePepFetch(getIndustries);
  const { data: products } = usePepFetch(getProducts);

  const industry = useMemo(
    () => (industries ?? []).find((i) => i.slug === slug) ?? null,
    [industries, slug],
  );
  const editorial = editorialForIndustry(slug);

  if (loading && !industry) {
    return (
      <SiteLayout>
        <div className="container py-5">{copy.loading}</div>
      </SiteLayout>
    );
  }

  if (error) {
    return (
      <SiteLayout>
        <div className="container py-5">
          <PepApiErrorBanner error={error} onRetry={reload} />
        </div>
      </SiteLayout>
    );
  }

  if (!industry) {
    return (
      <SiteLayout>
        <div className="container py-5">
          <p>{copy.notFound}</p>
          <Link to="/industries">{copy.backToSectors}</Link>
        </div>
      </SiteLayout>
    );
  }

  const name = en
    ? industry.displayName
    : (industry.displayNameFr ?? industry.displayName);
  const lead = editorial
    ? en
      ? editorial.summaryEn
      : editorial.summaryFr
    : en
      ? industry.description
      : (industry.longDescriptionFr ?? industry.descriptionFr ?? industry.description);

  const related = publishableRelated(industry, products ?? []);
  const trialSlugs = filterTrialEligibleProducts(related).map((p) => p.slug);
  const configureHref = `/configure?industry=${slug}`;
  const showBiometricNote = needsBiometricHardware(trialSlugs);
  const challenges = editorial
    ? editorial.axes.map((a) => (en ? `${a.titleEn}: ${a.bodyEn}` : `${a.titleFr}: ${a.bodyFr}`))
    : industry.challenges;
  const keywords = editorial
    ? [...(en ? editorial.seoTermsEn : editorial.seoTermsFr)]
    : industry.seoKeywords;

  return (
    <SiteLayout>
      <SeoHead
        title={en ? `Industry ${name} | ITaaS Suite` : `Secteur ${name} | ITaaS Suite`}
        description={lead.slice(0, 160)}
        keywords={keywords}
        canonicalPath={`/industries/${slug}`}
        locale={locale}
      />
      <div className="pep-sec-detail-hero">
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb pep-breadcrumb mb-3">
              <li className="breadcrumb-item">
                <Link to="/industries">{copy.breadcrumb}</Link>
              </li>
              <li className="breadcrumb-item active">{name}</li>
            </ol>
          </nav>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <h1 className="pep-sec-detail-hero__title mb-0">{name}</h1>
            {editorial?.showPublicLimitedBadge ? (
              <span className="pep-sec-badge pep-sec-badge--limited">{copy.limitedAvailability}</span>
            ) : null}
          </div>
          {editorial?.offerBadges ? (
            <div className="pep-sec-offer-badges mb-3">
              {editorial.offerBadges.map((b) => (
                <span key={b.slug} className="pep-sec-offer-badge pep-sec-offer-badge--limited">
                  {en ? b.labelEn : b.labelFr}
                  {b.badge === "limited" ? <span>{copy.limitedAvailability}</span> : null}
                </span>
              ))}
            </div>
          ) : null}
          <p className="pep-sec-detail-hero__lead">{lead}</p>
        </div>
      </div>
      <div className="container py-5">
        <div className="row g-5">
          {challenges && challenges.length > 0 && (
            <div className="col-md-6">
              <h2 className="h4 fw-bold mb-3">{copy.challengesTitle}</h2>
              <ul className="list-unstyled">
                {challenges.map((c) => (
                  <li className="mb-2" key={c}>
                    <span className="text-primary me-2">▸</span>
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {editorial && (
            <div className="col-md-6">
              <h2 className="h4 fw-bold mb-3">{copy.keySolutions}</h2>
              <SolutionChips solutions={editorial.solutions} locale={locale} />
            </div>
          )}
        </div>

        {trialSlugs.length > 0 && (
          <section className="mt-5 p-4 rounded-4 border border-primary border-opacity-25 bg-primary bg-opacity-10">
            <div className="row align-items-center g-3">
              <div className="col-lg-8">
                <h2 className="h5 fw-bold mb-2">
                  {en ? `Start with ${name}` : `Démarrer — secteur ${name}`}
                </h2>
                <p className="text-secondary small mb-0">
                  {en
                    ? "Compose your environment with recommended modules for this sector."
                    : "Composez votre environnement avec les modules recommandés pour ce secteur."}
                  {showBiometricNote && slug === "education" && (
                    <span className="d-block mt-2">
                      {en
                        ? "Biometric attendance modules include 1 reader by default; we recommend 1 reader per classroom."
                        : "Les modules avec pointage biométrique incluent par défaut 1 lecteur ; nous recommandons 1 lecteur par salle de classe."}
                    </span>
                  )}
                </p>
              </div>
              <div className="col-lg-4 text-lg-end">
                <Link className="btn btn-primary btn-lg" to={configureHref}>
                  {en ? "Create my environment →" : "Créer mon environnement →"}
                </Link>
              </div>
            </div>
          </section>
        )}

        {slug === "education" && (
          <section className="mt-4 p-4 rounded-4 border border-success border-opacity-25 bg-success bg-opacity-10">
            <div className="row align-items-center g-3">
              <div className="col-lg-8">
                <h2 className="h5 fw-bold mb-2">
                  {en ? "Education ambassador program" : "Programme ambassadeur éducation"}
                </h2>
                <p className="text-secondary small mb-0">
                  {en
                    ? "Teachers, parents, and schools can share a referral link and earn a commission when a school pays."
                    : "Enseignants, parents et écoles peuvent partager un lien et toucher une commission quand une école paie."}
                </p>
              </div>
              <div className="col-lg-4 text-lg-end">
                <Link className="btn btn-success" to="/affiliation/inscription?product=edu">
                  {en ? "Become an ambassador →" : "Devenir ambassadeur →"}
                </Link>
              </div>
            </div>
          </section>
        )}

        <section className="mt-5">
          <h2 className="h4 fw-bold mb-3">{copy.recommendedTitle}</h2>
          <div className="row g-3">
            {related.map((p) => (
              <div className="col-md-4" key={p.slug}>
                <IndustryProductCard product={p} industrySlug={slug} />
              </div>
            ))}
          </div>
        </section>

        <div className="mt-5 p-4 rounded-4 bg-body-tertiary text-center">
          <h3 className="h5 fw-bold">
            {en ? `Project in ${name}?` : `Projet dans le secteur ${name} ?`}
          </h3>
          <p className="text-secondary mb-3">
            {en
              ? "Our teams support deployments in the DRC and Central Africa."
              : "Nos équipes accompagnent les déploiements en RDC et en Afrique centrale."}
          </p>
          <div className="d-flex flex-wrap justify-content-center gap-2">
            <Link className="btn btn-primary" to={configureHref}>
              {en ? "Create my environment" : "Créer mon environnement"}
            </Link>
            <Link className="btn btn-outline-secondary" to={`/contact?industry=${slug}`}>
              {en ? "Request a demo" : "Demander une démo"}
            </Link>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}
